import React from 'react';
import { ArrowRight, Code, Zap, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import CodeRain from './CodeRain';
import AnimatedSection from './AnimatedSection';

const Hero = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center bg-black text-white overflow-hidden">
      {/* Animated Glass Gradient Background */}
      <div className="absolute inset-0 opacity-30">
        <div className="glass-gradient-1 absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-500/20 via-purple-500/10 to-transparent rounded-full blur-3xl animate-float"></div>
        <div className="glass-gradient-2 absolute top-1/4 right-0 w-80 h-80 bg-gradient-to-bl from-yellow-500/15 via-orange-500/10 to-transparent rounded-full blur-3xl animate-float-delayed"></div>
        <div className="glass-gradient-3 absolute bottom-0 left-1/4 w-72 h-72 bg-gradient-to-tr from-green-500/15 via-teal-500/10 to-transparent rounded-full blur-3xl animate-float-slow"></div>
        <div className="glass-gradient-4 absolute bottom-1/4 right-1/4 w-64 h-64 bg-gradient-to-tl from-pink-500/20 via-purple-500/10 to-transparent rounded-full blur-3xl animate-pulse-slow"></div>
      </div>

      {/* Glass Morphism Overlays */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-20 w-40 h-40 bg-white/5 backdrop-blur-xl rounded-full animate-drift-right"></div>
        <div className="absolute top-40 right-32 w-32 h-32 bg-white/3 backdrop-blur-lg rounded-full animate-drift-left"></div>
        <div className="absolute bottom-32 left-1/3 w-36 h-36 bg-white/4 backdrop-blur-xl rounded-full animate-drift-up"></div>
      </div>

      {/* Code Rain Background */}
      <CodeRain />

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Main Content Glass Container */}
        <div className="text-center relative">
          {/* Glass morphism backdrop for main content */}
          <div className="absolute inset-0 bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl shadow-black/25 -m-8 animate-pulse-slow"></div>
          {/* Badge */}
          <AnimatedSection delay={200} animation="fadeInUp">
            <div className="flex justify-center mb-8 relative z-10">
              <Badge className="bg-white/10 backdrop-blur-xl text-white border-white/20 px-6 py-3 text-sm animate-gradient-shift shadow-lg">
                <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-gradient-shift">
                  🚀 Leading Software Development Company in Sri Lanka
                </span>
              </Badge>
            </div>
          </AnimatedSection>

          {/* Main Heading */}
          <AnimatedSection delay={400} animation="fadeInUp">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight relative z-10">
              <span className="text-white animate-fade-in-word drop-shadow-lg" style={{ animationDelay: '0.5s' }}>Innovative</span>
              <br />
              <span className="text-gray-200 animate-fade-in-word drop-shadow-lg" style={{ animationDelay: '0.7s' }}>Software Solutions</span>
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-fade-in-word animate-gradient-shift drop-shadow-lg" style={{ animationDelay: '0.9s' }}>
                for Modern Business
              </span>
            </h1>
          </AnimatedSection>

          {/* Subtitle */}
          <AnimatedSection delay={600} animation="fadeInUp">
            <p className="text-xl md:text-2xl text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed">
              We craft cutting-edge web applications, mobile apps, and enterprise solutions 
              that drive digital transformation for businesses across Sri Lanka and beyond.
            </p>
          </AnimatedSection>

          {/* Feature Pills */}
          <AnimatedSection delay={800} animation="fadeInUp">
            <div className="flex flex-wrap justify-center gap-4 mb-12 relative z-10">
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-xl rounded-full px-4 py-2 border border-white/20 transform hover:scale-105 transition-all duration-300 hover:bg-white/15 shadow-lg">
                <Code size={18} className="text-blue-400 animate-pulse" />
                <span className="text-white text-sm font-medium">Custom Development</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-xl rounded-full px-4 py-2 border border-white/20 transform hover:scale-105 transition-all duration-300 hover:bg-white/15 shadow-lg" style={{ animationDelay: '0.2s' }}>
                <Zap size={18} className="text-yellow-400 animate-pulse" />
                <span className="text-white text-sm font-medium">Fast Delivery</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-xl rounded-full px-4 py-2 border border-white/20 transform hover:scale-105 transition-all duration-300 hover:bg-white/15 shadow-lg" style={{ animationDelay: '0.4s' }}>
                <Shield size={18} className="text-green-400 animate-pulse" />
                <span className="text-white text-sm font-medium">Secure Solutions</span>
              </div>
            </div>
          </AnimatedSection>

          {/* CTA Buttons */}
          <AnimatedSection delay={1000} animation="scaleIn">
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center relative z-10">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 text-lg group animate-bounce-subtle shadow-xl shadow-yellow-500/25 backdrop-blur-sm"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Start Your Project
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="border-2 border-white/30 text-white hover:text-white hover:border-white/50 px-8 py-4 rounded-full transition-all duration-300 text-lg bg-white/5 backdrop-blur-xl hover:scale-105 hover:bg-white/10 shadow-lg"
                onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
              >
                View Our Work
              </Button>
            </div>
          </AnimatedSection>

          {/* Stats */}
          <AnimatedSection delay={1200} animation="fadeInUp">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 pt-12 border-t border-gray-800">
              {[
                { value: "35+", label: "Projects Completed" },
                { value: "34+", label: "Happy Clients" },
                { value: "3+", label: "Years Experience" },
                { value: "5+", label: "Team Members" }
              ].map((stat, index) => (
                <div key={index} className="text-center group hover:scale-110 transition-transform duration-300">
                  <div className="text-3xl md:text-4xl font-bold text-white mb-2 animate-count-up">
                    {stat.value}
                  </div>
                  <div className="text-gray-400 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </div>

      {/* Scroll Indicator */}
      <AnimatedSection delay={1400} animation="fadeInUp">
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-gray-400 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </AnimatedSection>
    </section>
  );
};

export default Hero;